import React, { ReactElement, useCallback, useMemo, useState } from 'react'
import { useAtomValue, useAtom } from 'infrastructure/state/jotai.wrapper'
import {
  addingDisabledAtom,
  isAddingAtom,
  mutationFieldsAtom,
  updatingIdAtom,
} from './atoms'
import { fieldColumnType, NEW_ITEM_ID } from './types'
import {
  StringOrNumberDisplay,
  StringOrNumberEdit,
} from './updatableColumns/StringOrNumberColumn'
import { UpdateColumn } from './updatableColumns/UpdateColumn'
import { PartnerDisplay, PartnerEdit } from './updatableColumns/PartnerColumn'
import { SubjectDisplay, SubjectEdit } from './updatableColumns/SubjectColumn'
import { GradeDisplay, GradeEdit } from './updatableColumns/GradeColumn'
import { NoPropagationButton } from '../../utility/NoPropagationButton'
import { ItemCountColumn } from './updatableColumns/ItemCountColumn'
import { useToggleState } from 'shared/hooks'
import { byStringFieldSort } from '../../Helpers'
import { Box, Button } from '@mui/material'
import { SortingArrows } from 'shared/ui/Table/SortingArrows'
import { PotentiallySortableColumn } from 'shared/ui/Table/types'
import { ProductType } from '../products/types'
import { ProductGroupDisplay } from './updatableColumns/ProductGroupColumn'
import { groupsByProductId } from '../../../api/masters.atoms'
import {
  AssignableColumn,
  AssignableEdit,
} from './updatableColumns/AssignableColumn'
import {
  FrameworkIdDisplay,
  FrameworkIdEdit,
} from './updatableColumns/FrameworkIdColumn'
import { CrosswalkStatusColumn } from './updatableColumns/ScopeAndSequenceColumns'
import { CrosswalkAlignmentDisplay } from './updatableColumns/CrosswalkAlignmentsColumn'

export function useTableColumns<T extends { id: number }>() {
  const fields = useAtomValue(mutationFieldsAtom)
  const fieldFunc = useColumnForField()
  return [
    {
      title: 'id',
      width: 50,
      rowContent({ id }: T) {
        return <IdColumn id={id} />
      },
      sortable: 'id',
    },
    ...(fields?.map(fieldFunc) ?? []),
    {
      title: 'Update',
      width: 100,
      rowContent({ id }: T) {
        return <UpdateColumn id={id} />
      },
    },
  ]
}

function useColumnForField() {
  const groupSorter = useProductGroupSort()
  return useCallback(
    (field: fieldColumnType) => {
      const { valueType } = field
      switch (valueType) {
        case 'string':
        case 'number': {
          return {
            ...tableColumnData({
              columnData: field,
              Show: StringOrNumberDisplay,
              Edit: StringOrNumberEdit,
            }),
            sortable: field.key,
          }
        }
        case 'partner': {
          return {
            ...tableColumnData({
              columnData: field,
              Show: PartnerDisplay,
              Edit: PartnerEdit,
            }),
            sortable: field.key,
            customSort: productPartnerSort,
          }
        }
        case 'productGroup': {
          return {
            ...tableColumnData({
              columnData: field,
              Show: ProductGroupDisplay,
              Edit: ProductGroupDisplay,
            }),
            sortable: field.key,
            customSort: groupSorter,
          }
        }
        case 'subject': {
          return tableColumnData({
            columnData: field,
            Show: SubjectDisplay,
            Edit: SubjectEdit,
          })
        }
        case 'grade': {
          return {
            ...tableColumnData({
              columnData: field,
              Show: GradeDisplay,
              Edit: GradeEdit,
            }),
            sortable: field.key,
          }
        }
        case 'unassignable_at': {
          return {
            ...tableColumnData({
              columnData: field,
              Show: AssignableColumn,
              Edit: AssignableEdit,
            }),
            sortable: field.key,
          }
        }
        case 'itemCount': {
          return tableColumnData({
            columnData: field,
            Show: ItemCountColumn,
            Edit: ItemCountColumn,
          })
        }
        case 'crosswalkAlignments': {
          return tableColumnData({
            columnData: field,
            Show: CrosswalkAlignmentDisplay,
            Edit: CrosswalkAlignmentDisplay,
          })
        }
        case 'frameworkId': {
          return tableColumnData({
            columnData: field,
            Show: FrameworkIdDisplay,
            Edit: FrameworkIdEdit,
          })
        }
        case 'crosswalkStatus': {
          return tableColumnData({
            columnData: field,
            Show: StringOrNumberDisplay,
            Edit: CrosswalkStatusColumn,
          })
        }
      }
      throw new Error(`Unrecognized value type ${valueType}`)
    },
    [groupSorter],
  )
}

function IdColumn({ id }: { id: string | number }) {
  const [isAdding, setAdding] = useAtom(isAddingAtom)
  const disableAdd = useAtomValue(addingDisabledAtom)
  if (id !== NEW_ITEM_ID) return <>{id}</>
  if (isAdding) {
    return <>NEW</>
  }
  return (
    <NoPropagationButton disabled={disableAdd} onClick={() => setAdding(true)}>
      New
    </NoPropagationButton>
  )
}

function tableColumnData<T extends { id: number }>({
  columnData,
  Show,
  Edit,
}: {
  columnData: fieldColumnType
  Show: (p: { item: T; columnData: fieldColumnType }) => ReactElement | null
  Edit: (p: { item: T; columnData: fieldColumnType }) => ReactElement | null
}) {
  const ItemRender = ({ item }: { item: T }) => {
    const showDisplay = useColumnDisplayState(item.id)
    if (showDisplay === 'showValue') {
      return <Show item={item} columnData={columnData} />
    } else if (showDisplay === 'editing') {
      return <Edit item={item} columnData={columnData} />
    }
    return null
  }

  return {
    title: columnData.key,
    width: 100,
    rowContent(item: T) {
      return <ItemRender item={item} />
    },
  }
}

type columnDisplayState = 'showValue' | 'empty' | 'editing'

export function useColumnDisplayState(id: number): columnDisplayState {
  const isAdding = useAtomValue(isAddingAtom)
  const currentEditId = useAtomValue(updatingIdAtom)
  switch (true) {
    case id === NEW_ITEM_ID && isAdding:
    case id === currentEditId:
      return 'editing'
    case id === NEW_ITEM_ID && !isAdding:
      return 'empty'
    default:
      return 'showValue'
  }
}

export function useSortableColumns<T>(
  items: T[],
  columns: PotentiallySortableColumn<T>[],
) {
  const [sortField, setSortField] = useState('')
  const [sortAscending, toggleSortAscending] = useToggleState(true)
  const [sorter, setSorter] = useState<((a: T, b: T) => number) | undefined>(
    undefined,
  )

  const columnsWithSort = useMemo(() => {
    return columns.map((col) => {
      if (!col.sortable) return col

      const doSort = () => {
        if (!col.sortable) return
        if (sortField === col.sortable) {
          toggleSortAscending()
        } else {
          setSortField(col.sortable)
          setSorter(() => col.customSort || byStringFieldSort(col.sortable!))
          toggleSortAscending(true)
        }
      }

      return {
        ...col,
        title: (
          <Box display="flex" flexDirection="row" alignItems="center">
            <Button
              style={{ fontWeight: sortField === col.sortable ? 'bold' : '' }}
              onClick={doSort}
            >
              {col.title}
            </Button>
            <Box>
              <SortingArrows
                isSorting={sortField === col.sortable}
                isAscending={sortAscending}
              />
            </Box>
          </Box>
        ),
      }
    })
  }, [columns, sortField, sortAscending, toggleSortAscending])

  return useMemo(() => {
    const dupList = [...items]
    const sorted = sorter ? dupList.sort(sorter) : dupList
    if (!sortAscending) sorted.reverse()
    return {
      sorted,
      columns: columnsWithSort,
    }
  }, [items, columnsWithSort, sortAscending, sorter])
}

function productPartnerSort(p1: ProductType, p2: ProductType) {
  const partner1 = p1.partner?.id ?? 0
  const partner2 = p2.partner?.id ?? 0
  return partner1 - partner2
}

function useProductGroupSort() {
  const groups = useAtomValue(groupsByProductId)
  return useCallback(
    (p1: ProductType, p2: ProductType) => {
      const g1 = groups[p1.id]?.id ?? 0
      const g2 = groups[p2.id]?.id ?? 0
      return g1 - g2
    },
    [groups],
  )
}
